import authService, { TOKEN_TYPES } from "./authService";
import { toBackendTrialId } from "@/utils/trialIdMap";
import type {
  CdrCycleEvent,
  CdrCycleState,
  CdrMarkResult,
  CdrPolicySettings,
  CdrPolicyUpdate,
  CdrPolicyVersion,
  CdrReviewItemDetail,
} from "@/views/ClinicalReview/clinicalReview.types";

/** Client for the Clinical Data Review endpoints (`/data/trial/<id>/cdr/…`).
 *
 * The listing itself is NOT here — it is the unified `cdr_review_items`
 * dataset, served through `MetadataDashboardTemplate`/`DashboardFetchWrapper`
 * like every other dataset screen. This client covers only what the template
 * cannot do: the record drawer, dispositions, and the policy tab.
 */

const base = (trialId: string) =>
  `${import.meta.env.VITE_API_URL}/data/trial/${toBackendTrialId(trialId)}/cdr`;

export class CdrReviewApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "CdrReviewApiError";
    this.status = status;
  }
}

async function request<T>(
  url: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
  const response = await fetch(url, {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail =
      (payload && typeof payload.error === "string" && payload.error) ||
      (payload && typeof payload.detail === "string" && payload.detail) ||
      `Request failed (${response.status})`;
    throw new CdrReviewApiError(detail, response.status);
  }
  return payload as T;
}

const cdrReviewApi = {
  item: (trialId: string, itemId: number): Promise<CdrReviewItemDetail> =>
    request<CdrReviewItemDetail>(`${base(trialId)}/items/${itemId}/`),

  mark: (
    trialId: string,
    body: {
      items: { item_id: number; material_hash: string }[];
      action: "reviewed_no_issue" | "observation" | "escalated";
      comment?: string;
    },
  ): Promise<CdrMarkResult> =>
    request<CdrMarkResult>(`${base(trialId)}/marks/`, { method: "POST", body }),

  policy: (trialId: string): Promise<CdrPolicySettings> =>
    request<CdrPolicySettings>(`${base(trialId)}/policy/`),

  savePolicy: (
    trialId: string,
    body: CdrPolicyUpdate,
  ): Promise<CdrPolicySettings & { changes: string[] }> =>
    request(`${base(trialId)}/policy/`, { method: "PUT", body }),

  policyHistory: (trialId: string): Promise<{ versions: CdrPolicyVersion[] }> =>
    request(`${base(trialId)}/policy/history/`),

  cycle: (trialId: string): Promise<CdrCycleState> =>
    request<CdrCycleState>(`${base(trialId)}/cycle/`),

  /** Sign a cycle off, or reopen one. Both are appended to the cycle's
   * ledger; neither edits what is already recorded there. */
  cycleAction: (
    trialId: string,
    body: { action: "sign_off" | "reopen"; cycle_id?: number; reason?: string },
  ): Promise<{ event: CdrCycleEvent; cycle_seq: number }> =>
    request(`${base(trialId)}/cycle/action/`, { method: "POST", body }),
};

export default cdrReviewApi;
